import {zodResolver} from '@hookform/resolvers/zod'
import {Controller, useForm} from 'react-hook-form'
import {z} from 'zod'
import {FollowUp, OnSubmitFollowUp} from '@/features/treatment-requests/types'
import dayjs from 'dayjs'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {Label} from '@/components/ui/label/Label'
import {TextArea} from '@/components/commons/textarea/TextArea'
import {Button} from '@/components/ui/button/Button'
import i18n, {useTranslation} from '@/translations/i18n'
import {Spinner} from '@/components/ui/spinner/Spinner'
import {getDirtyFieldsFromFormValues, ObjectKeys} from '@/utilities/helpers'
import {InputDate} from '@/components/commons/input-date/InputDate'

const TreatmentRequestFollowUpFormValidationSchema = z.object({
    date: z.date({required_error: i18n.t('errors:required'), invalid_type_error: i18n.t('errors:invalid_date')}),
    notes: z.string().nullish()
})
type TreatmentRequestFollowUpFormValidationSchema = z.infer<typeof TreatmentRequestFollowUpFormValidationSchema>

export const TreatmentRequestFollowUpForm = <T extends Partial<FollowUp>>({
    defaultValues,
    isLoading,
    disabledDate = false,
    onCancel,
    onSubmit
}: {
    defaultValues?: T
    isLoading?: boolean
    disabledDate?: boolean
    onCancel: () => void
    onSubmit: (values: OnSubmitFollowUp<T>) => void
}) => {
    const {t} = useTranslation()

    const form = useForm<TreatmentRequestFollowUpFormValidationSchema>({
        mode: 'onBlur',
        resolver: zodResolver(TreatmentRequestFollowUpFormValidationSchema),
        defaultValues: {
            date: defaultValues?.date ? new Date(defaultValues.date) : undefined,
            notes: defaultValues?.notes
        }
    })

    const handleSubmit = form.handleSubmit(values =>
        onSubmit(
            defaultValues
                ? (getDirtyFieldsFromFormValues(form.formState.dirtyFields, values) as OnSubmitFollowUp<T>)
                : values
        )
    )

    return (
        <Flexbox render="form" onSubmit={handleSubmit} direction="column" gap={3} fullWidth>
            <Controller
                name="date"
                control={form.control}
                render={({field}) => (
                    <InputDate
                        label={t('commons:date')}
                        placeholder="GG/MM/AAAA"
                        errorMessage={form.formState.errors.date?.message}
                        disableAfterDate={dayjs().toDate()}
                        value={field.value}
                        onChange={field.onChange}
                        disabled={disabledDate}
                        fullWidth
                    />
                )}
            />

            <Flexbox direction="column" gap={1.5} fullWidth>
                <Label>
                    {t('commons:additional_info')} <span className="description">{t('commons:optional')}</span>
                </Label>
                <TextArea placeholder={t('treatment_requests:write_your_text')} {...form.register('notes')} />
            </Flexbox>

            <Flexbox fullWidth justify="end" gap={2}>
                <Button variant="secondary" onClick={onCancel}>
                    {t('commons:cancel')}
                </Button>
                <Button
                    type="submit"
                    variant="primaryNeutral"
                    disabled={!ObjectKeys(form.formState.dirtyFields).length || isLoading}
                >
                    {isLoading && <Spinner />}
                    {t('commons:save')}
                </Button>
            </Flexbox>
        </Flexbox>
    )
}
